import React, { useState, useCallback } from 'react';
import { Trash2, X, History, Plus, ChevronLeft, MessageSquare, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useBusinessContext } from '@/context/BusinessContext';
import { cn } from '@/lib/core/utils';
import { AI_ASSISTANT } from '@/lib/ai-chat/constants';
import { AIMessageList } from './AIMessageList';
import { AIMessageInput } from './AIMessageInput';
import { useAIChat } from './useAIChat';
import { useMobileAIChat } from '@/context/MobileAIChatContext';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import type { AIToolCall } from './types';

// Format relative time for conversation list
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

interface MobileAIChatOverlayProps {
  businessId: string | null;
}

export const MobileAIChatOverlay: React.FC<MobileAIChatOverlayProps> = ({ businessId }) => {
  const isMobile = useIsMobile();
  const { isOpen, closeAIChat } = useMobileAIChat();
  const { session } = useAuth();
  const { selectedBusiness } = useBusinessContext();
  const {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
    confirmTools,
    sendQuestionAnswers,
    conversationSummaries,
    activeConversationId,
    createNewConversation,
    switchConversation,
    deleteConversation,
    usage,
    stopGeneration,
    retryLastMessage,
    canRetry,
    wasStopped,
  } = useAIChat(businessId, session?.access_token);

  // View state: 'chat' or 'history'
  const [view, setView] = useState<'chat' | 'history'>('chat');

  // Get current conversation name
  const currentConversation = conversationSummaries.find(c => c.id === activeConversationId);
  const conversationTitle = currentConversation?.title || 'New Chat';

  // Handle selecting a conversation from history
  const handleSelectConversation = (id: string) => {
    switchConversation(id);
    setView('chat');
  };

  // Handle creating a new conversation
  const handleNewChat = () => {
    createNewConversation();
    setView('chat');
  };

  // Handle deleting a conversation
  const handleDeleteConversation = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    deleteConversation(id);
  };

  // Handle reverting a create operation
  const handleRevert = useCallback(async (toolCall: AIToolCall) => {
    const resourceId = toolCall.result?.shift?.id || toolCall.result?.employee?.id;
    const resourceType = toolCall.result?.shift ? 'shift' : 'employee';

    if (!resourceId) {
      console.error('Cannot revert: no resource ID found in tool result');
      return;
    }

    await sendMessage(`Please delete the ${resourceType} with ID ${resourceId} that was just created.`);
  }, [sendMessage]);

  // Handle suggestion click from welcome screen
  const handleSuggestionClick = useCallback((text: string) => {
    sendMessage(text);
  }, [sendMessage]);

  // Only render on mobile when open
  if (!isMobile || !isOpen) return null;

  return (
    <div
      className={cn(
        "fixed inset-x-0 top-0 bottom-16 z-[60] bg-background flex flex-col",
        "animate-in slide-in-from-bottom duration-300"
      )}
    >
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 border-b bg-muted/30 safe-area-pt">
        <div className="flex items-center gap-2 min-w-0">
          {view === 'history' ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => setView('chat')}
                title="Back to chat"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <span className="text-base font-medium">Chat History</span>
            </>
          ) : (
            <>
              <div className="w-7 h-7 rounded-full flex items-center justify-center ring-1 ring-border shrink-0">
                <img
                  src="/media/tag-innovation-logo.png"
                  alt={AI_ASSISTANT.name}
                  className="h-4 w-4 rounded-sm"
                />
              </div>
              <span className="text-base font-medium truncate">{conversationTitle}</span>
              {usage && (
                <span
                  className="text-xs text-muted-foreground ml-1 shrink-0"
                  title={`${usage.totalTokens.toLocaleString()} / ${usage.contextWindow.toLocaleString()} tokens`}
                >
                  {usage.contextUsedPercent}%
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {view === 'chat' && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleNewChat}
                title="New chat"
              >
                <Plus className="h-4 w-4" />
              </Button>
              {conversationSummaries.length > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setView('history')}
                  title="Chat history"
                >
                  <History className="h-4 w-4" />
                </Button>
              )}
              {messages.length > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={clearMessages}
                  title="Clear chat"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={closeAIChat}
            title="Close AI Assistant"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </header>

      {view === 'history' ? (
        /* History View */
        <div className="flex-1 overflow-y-auto">
          {conversationSummaries.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-6">
              <MessageSquare className="h-12 w-12 mb-3 opacity-50" />
              <p className="text-base">No chat history yet</p>
              <p className="text-sm mt-1">Start a conversation to see it here</p>
            </div>
          ) : (
            <div className="p-3 space-y-2">
              {conversationSummaries.map((conv) => {
                const isActive = conv.id === activeConversationId;
                return (
                  <div
                    key={conv.id}
                    onClick={() => handleSelectConversation(conv.id)}
                    className={cn(
                      'group flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors',
                      'hover:bg-muted active:bg-muted/80',
                      isActive && 'bg-primary/10 border-l-2 border-primary'
                    )}
                  >
                    <MessageSquare className={cn(
                      'h-5 w-5 shrink-0',
                      isActive ? 'text-primary' : 'text-muted-foreground'
                    )} />
                    <div className="flex-1 min-w-0">
                      <p className={cn(
                        'text-sm truncate',
                        isActive ? 'font-medium' : ''
                      )}>{conv.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {conv.messageCount} messages · {formatRelativeTime(conv.updatedAt)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 opacity-40 hover:opacity-100 transition-opacity"
                      onClick={(e) => handleDeleteConversation(e, conv.id)}
                      title="Delete conversation"
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* Chat View */
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4">
            {!businessId ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <div className="w-12 h-12 rounded-full flex items-center justify-center ring-1 ring-border mb-3">
                  <img
                    src="/media/tag-innovation-logo.png"
                    alt={AI_ASSISTANT.name}
                    className="h-7 w-7 rounded-sm"
                  />
                </div>
                <p className="text-base">Select a business first</p>
                <p className="text-sm mt-1">Go to your dashboard and select a business</p>
              </div>
            ) : (
              <AIMessageList
                messages={messages}
                isLoading={isLoading}
                onRevert={handleRevert}
                onConfirmTools={confirmTools}
                onSuggestionClick={handleSuggestionClick}
                businessLogoUrl={selectedBusiness?.favicon_url}
                businessId={businessId}
                onQuestionsSubmit={sendQuestionAnswers}
              />
            )}
          </div>

          {/* Error display with retry button */}
          {(error || wasStopped) && (
            <div className="px-4 py-2 bg-destructive/10 text-destructive text-sm flex items-center justify-between gap-2">
              <span>{error || 'Generation stopped'}</span>
              {canRetry && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={retryLastMessage}
                  className="h-7 px-2 text-xs"
                >
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Retry
                </Button>
              )}
            </div>
          )}

          {/* Input */}
          <div>
            <AIMessageInput
              onSend={sendMessage}
              onStop={stopGeneration}
              disabled={isLoading || !businessId}
              isLoading={isLoading}
              placeholder={
                businessId
                  ? 'Ask about your business data...'
                  : 'Select a business first'
              }
            />
          </div>
        </>
      )}
    </div>
  );
};

export default MobileAIChatOverlay;
